import React, { useLayoutEffect, useRef } from "react";
import { rgbOf } from "./paintCount";
import { paletteAt } from "./reveal";

// PROTOTYPE (hero shapes): Cerchio's clients (see dotShapes.js) on one
// canvas, at the layout's own size in CSS pixels, each a dot in its colour
// as the disc fills (reveal.js), `met` and `gold` from the story; or,
// where a layout's dot names its own tone (Costellazione's, see
// transitionFrames.js), in that. The colours come from the page's tokens. It
// is hidden from assistive technology, which reads the numbers in the
// caption. Until its box is measured there is no layout, and it takes no
// room. With `apart`, where the clients move over the ones still waiting
// (Costellazione), each keeps a moat of the stage round it; a dot `above`
// the others, a client flying over them, is painted over them all, each in
// its own moat, so it shows on its own as it passes. `over`, if
// given, paints over the clients (Costellazione's ball and count): it is
// handed the canvas's context, in CSS pixels, and its device pixels to one.
// `glow`, if given, warms the clients still waiting, each as far, 0 to 1,
// as it says for the dot, turning it from grey to gold as far (the hero's
// invitation, invite.js); they stay waiting, drawn under the others.
export default function DotCanvas({ layout, met, gold, apart = false, over, glow, className }) {
  const canvasRef = useRef(null);
  const coloursRef = useRef(null);

  useLayoutEffect(() => {
    const style = getComputedStyle(canvasRef.current);
    const token = (name) => style.getPropertyValue(name).trim();
    coloursRef.current = { met: token("--fg"), waiting: token("--hv-dot-off"), lit: token("--gold"), stage: token("--stage") };
  }, []);

  useLayoutEffect(() => {
    if (layout) drawDots(canvasRef.current, layout, colouring(layout, paletteAt({ met, gold }, coloursRef.current), coloursRef.current), coloursRef.current, { apart, over, glow });
  }, [layout, met, gold, apart, over, glow]);

  const size = layout ? { width: layout.width, height: layout.height } : { width: 0, height: 0 };
  return <canvas ref={canvasRef} className={className} aria-hidden="true" style={size} />;
}

// How much wider than a dot its moat is.
export const MOAT = 1.5;
// The layers the clients are painted in, the first under the others.
const WAITING = 0;
const RESTING = 1;
const ABOVE = 2;

// Each dot's colour: its own tone's, if it names one, or the palette's.
function colouring(layout, palette, colours) {
  return (i) => {
    const { tone } = layout.dots[i];
    if (tone === undefined) return palette(i);
    if (!(tone in colours)) throw new Error(`DotCanvas: "${tone}" is not a tone the clients are painted in`);
    return colours[tone];
  };
}

// How finely a waiting client warms: in this many steps, so the clients
// next to each other share a colour and a fill.
const WARMTH_STEPS = 24;

// Each client's warmth, 0 to 1, as `glow` has it, in WARMTH_STEPS.
function warmthsOf(layout, glow) {
  return layout.dots.map((dot) => Math.round(Math.min(1, Math.max(0, glow(dot))) * WARMTH_STEPS) / WARMTH_STEPS);
}

// The colour `share` of the way from grey to gold.
function tinted(colours, share) {
  if (share === 0) return colours.waiting;
  if (share === 1) return colours.lit;
  const grey = rgbOf(colours.waiting);
  const gold = rgbOf(colours.lit);
  const [r, g, b] = grey.map((from, k) => Math.round(from + (gold[k] - from) * share));
  return `rgb(${r},${g},${b})`;
}

// In the order they fill, the ones still waiting first, so the others show
// over them, in their moats if `apart`, each warmed as `glow` has it; then
// the ones `above` them, one by one, each in its moat; one fill for each run
// of dots of the same colour; then whatever goes `over` them.
function drawDots(canvas, layout, colourOf, colours, { apart, over, glow }) {
  const ratio = window.devicePixelRatio || 1;
  const width = Math.round(layout.width * ratio);
  const height = Math.round(layout.height * ratio);
  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
  }
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("DotCanvas: the browser gave no 2D context for the clients");
  ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
  ctx.clearRect(0, 0, layout.width, layout.height);
  let run = null;
  // The `i`th dot drawn in `colour`, `across` times its size.
  const paint = (i, colour, across) => {
    if (colour !== run) {
      if (run !== null) ctx.fill();
      ctx.beginPath();
      ctx.fillStyle = colour;
      run = colour;
    }
    const at = layout.dots[i];
    const r = (layout.size * (at.k ?? 1) * across) / 2;
    ctx.moveTo(at.x + r, at.y);
    ctx.arc(at.x, at.y, r, 0, Math.PI * 2);
  };
  const layers = layout.dots.map((at, i) => (colourOf(i) === colours.waiting ? WAITING : at.above ? ABOVE : RESTING));
  const each = (layer, paintOne) => layers.forEach((dotLayer, i) => dotLayer === layer && paintOne(i));
  const warmths = glow && warmthsOf(layout, glow);
  each(WAITING, (i) => paint(i, warmths ? tinted(colours, warmths[i]) : colourOf(i), 1));
  if (apart) each(RESTING, (i) => paint(i, colours.stage, MOAT));
  each(RESTING, (i) => paint(i, colourOf(i), 1));
  each(ABOVE, (i) => {
    paint(i, colours.stage, MOAT);
    paint(i, colourOf(i), 1);
  });
  if (run !== null) ctx.fill();
  over?.(ctx, ratio);
}
